import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import {
  ChartBarIcon,
  UserGroupIcon,
  MapPinIcon,
  DocumentChartBarIcon,
  QrCodeIcon,
  TrophyIcon,
  DocumentTextIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import BrowserFrame from '../shared/BrowserFrame'
import { prefersReducedMotion } from '$javascript/utils/utils'

const Benefits = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion() ? 0 : 0.1,
        delayChildren: prefersReducedMotion() ? 0 : 0.2
      }
    }
  }

  const itemVariants = prefersReducedMotion()
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
        }
      }

  const imageVariants = prefersReducedMotion()
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.95 },
        visible: {
          opacity: 1,
          scale: 1,
          transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
        }
      }

  // Feature data
  const organizerFeatures = [
    {
      icon: ChartBarIcon,
      title: 'Real-Time Campaign Dashboard',
      description: 'Monitor all active campaigns, track completion rates, and see volunteer activity at a glance'
    },
    {
      icon: UserGroupIcon,
      title: 'Effortless Volunteer Management',
      description: 'Onboard new writers in seconds, track individual progress, and send automated reminders'
    },
    {
      icon: MapPinIcon,
      title: 'Smart Address Distribution',
      description: 'Automated address assignment, bulk uploads, and built-in duplicate prevention'
    },
    {
      icon: DocumentChartBarIcon,
      title: 'Powerful Analytics & Reports',
      description: 'Export detailed insights, track historical data, and measure campaign impact'
    }
  ]

  const volunteerFeatures = [
    {
      icon: QrCodeIcon,
      title: 'Mark Addresses Complete Instantly',
      description: 'Mark postcards and letters complete instantly by scanning QR codes with your phone'
    },
    {
      icon: ClockIcon,
      title: 'Request Addresses Anytime',
      description: 'Get new addresses 24/7 through our web portal - write on your schedule'
    },
    {
      icon: TrophyIcon,
      title: 'Track Your Impact',
      description: 'See your personal dashboard with completed postcards and campaign progress'
    },
    {
      icon: DocumentTextIcon,
      title: 'Writing Resources & Tips',
      description: 'Access message templates, writing guides, and best practices'
    }
  ]

  const FeatureBlock = ({ features, title, subtitle, imagePosition = 'right', sectionRef, imageSrc, imageAlt }) => {
    const isInView = useInView(sectionRef, { once: true, amount: 0.2 })

    return (
      <div className="container">
        {/* Content and Image Grid */}
        <div
          className="benefits-block"
          style={{
            display: 'grid',
            gridTemplateColumns: imagePosition === 'right' ? '3fr 7fr' : '7fr 3fr',
            alignItems: 'center',
            gap: 'var(--space-3xl)',
            position: 'relative'
          }}
        >
          {imagePosition === 'right' ? (
            <>
              {/* Left: Content */}
              <motion.div
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={containerVariants}
              >
                {/* Section Header */}
                <motion.div variants={itemVariants} style={{ marginBottom: 'var(--space-xl)' }}>
                  <p style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: '600',
                    color: 'var(--color-primary-600)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: 'var(--space-xs)'
                  }}>
                    {subtitle}
                  </p>
                  <h2 style={{
                    fontSize: 'var(--text-xl)',
                    fontWeight: '700',
                    color: 'var(--color-neutral-900)',
                    marginBottom: 'var(--space-md)',
                    lineHeight: '1.2'
                  }}>
                    {title}
                  </h2>
                </motion.div>

                {/* Feature List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  {features.map((feature, index) => {
                    const Icon = feature.icon
                    return (
                      <motion.div
                        key={index}
                        variants={itemVariants}
                        style={{
                          display: 'flex',
                          gap: 'var(--space-sm)',
                          alignItems: 'flex-start'
                        }}
                      >
                        <div style={{
                          flexShrink: 0,
                          width: '36px',
                          height: '36px',
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--gradient-blue)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Icon style={{
                            width: '18px',
                            height: '18px',
                            color: 'var(--color-primary-600)'
                          }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontSize: 'var(--text-base)',
                            fontWeight: '600',
                            color: 'var(--color-neutral-900)',
                            marginBottom: '2px'
                          }}>
                            {feature.title}
                          </h3>
                          <p style={{
                            fontSize: 'var(--text-sm)',
                            color: 'var(--color-neutral-600)',
                            lineHeight: '1.5'
                          }}>
                            {feature.description}
                          </p>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>

              {/* Right: Image */}
              <motion.div
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={imageVariants}
              >
                <BrowserFrame
                  imageUrl={imageSrc}
                  alt={imageAlt}
                />
              </motion.div>
            </>
          ) : (
            <>
              {/* Left: Image */}
              <motion.div
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={imageVariants}
              >
                <BrowserFrame
                  imageUrl={imageSrc}
                  alt={imageAlt}
                />
              </motion.div>

              {/* Right: Content */}
              <motion.div
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={containerVariants}
              >
                {/* Section Header */}
                <motion.div variants={itemVariants} style={{ marginBottom: 'var(--space-xl)' }}>
                  <p style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: '600',
                    color: 'var(--color-primary-600)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: 'var(--space-xs)'
                  }}>
                    {subtitle}
                  </p>
                  <h2 style={{
                    fontSize: 'var(--text-xl)',
                    fontWeight: '700',
                    color: 'var(--color-neutral-900)',
                    marginBottom: 'var(--space-md)',
                    lineHeight: '1.2'
                  }}>
                    {title}
                  </h2>
                </motion.div>

                {/* Feature List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  {features.map((feature, index) => {
                    const Icon = feature.icon
                    return (
                      <motion.div
                        key={index}
                        variants={itemVariants}
                        style={{
                          display: 'flex',
                          gap: 'var(--space-sm)',
                          alignItems: 'flex-start'
                        }}
                      >
                        <div style={{
                          flexShrink: 0,
                          width: '36px',
                          height: '36px',
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--gradient-blue)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Icon style={{
                            width: '18px',
                            height: '18px',
                            color: 'var(--color-primary-600)'
                          }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontSize: 'var(--text-base)',
                            fontWeight: '600',
                            color: 'var(--color-neutral-900)',
                            marginBottom: '2px'
                          }}>
                            {feature.title}
                          </h3>
                          <p style={{
                            fontSize: 'var(--text-sm)',
                            color: 'var(--color-neutral-600)',
                            lineHeight: '1.5'
                          }}>
                            {feature.description}
                          </p>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>
            </>
          )}
        </div>
      </div>
    )
  }

  const organizerRef = useRef(null)
  const volunteerRef = useRef(null)

  return (
    <section
      className='bg-white overflow-visible pt-20'
    >
      <div>
        {/* Section Header */}
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            style={{
              textAlign: 'center',
              marginBottom: 'var(--space-2xl)'
            }}
          >
            <motion.h2
              variants={itemVariants}
              style={{
                fontSize: 'var(--text-4xl)',
                fontWeight: '700',
                color: 'var(--color-neutral-900)',
                marginBottom: 'var(--space-lg)'
              }}
            >
              Everything You Need to Scale Your Impact
            </motion.h2>
            <motion.p
              variants={itemVariants}
              style={{
                fontSize: 'var(--text-xl)',
                color: 'var(--color-neutral-600)',
                maxWidth: '800px',
                margin: '0 auto'
              }}
            >
              Sincere gives organizers and volunteers the tools they need to run successful
              postcard and letter-writing campaigns with less effort and better results.
            </motion.p>
          </motion.div>
        </div>

        {/* For Organizers Block */}
        <div ref={organizerRef} style={{ marginBottom: 'var(--space-4xl)' }}>
          <FeatureBlock
            features={organizerFeatures}
            title="Do More With Less Work"
            subtitle="For Organizers"
            imagePosition="right"
            sectionRef={organizerRef}
            imageSrc="/images/sincere-features-organizer.jpg"
            imageAlt="Sincere organizer dashboard showing campaign management interface"
          />
        </div>

        {/* For Volunteers Block */}
        <div ref={volunteerRef}>
          <FeatureBlock
            features={volunteerFeatures}
            title="Write on Your Schedule"
            subtitle="For Volunteers"
            imagePosition="left"
            sectionRef={volunteerRef}
            imageSrc="/images/sincere-features-writer.jpg"
            imageAlt="Sincere volunteer interface showing address request and tracking features"
          />
        </div>
      </div>
    </section>
  )
}

export default Benefits